import type {
  RenewalAlert,
  RenewalStatus,
  RenewalSummary,
} from '@policyvision/contracts';

import { Client } from '@/common/services/Client';
import Paginated from '@/common/types/paginated';

import { Filters } from '../types/FilterTypes';
import { RenewalSortField, SortOrder } from '../types/SortTypes';

export interface RenewalResponse {
  uuid: string;
  expirationDate: string;
  alerts: string[] | null;
  notesCount: number;
  status: string;
  assignedToName: string | null;
  assignedToUuid: string | null;
  enterpriseName: string;
  policyNumber: string;
  type: string;
  insurerName: string;
  currentPremium: number;
  renewalPremium: number | null;
  premiumVariation: number | null;
  amendmentsTotal: number;
  claimsTotal: number;
  commissionPercentage: number | null;
  honoraryFee: number | null;
  insurerFee: number | null;
  policyGuid: string;
}

export type RenewalPaginationParams = {
  pageIndex: number;
  pageSize: number;
  sortBy: RenewalSortField;
  sortOrder: SortOrder;
};

export interface PaginatedRenewalsResponse {
  renewals: RenewalResponse[];
  totalPages: number;
  totalRenewals: number;
}

function mapToRenewal(response: RenewalResponse): RenewalSummary {
  return {
    uuid: response.uuid,
    expirationDate: new Date(response.expirationDate),
    alerts: response.alerts as RenewalAlert[] | null,
    notesCount: response.notesCount,
    status: response.status as RenewalStatus,
    assignedToName: response.assignedToName,
    assignedToUuid: response.assignedToUuid,
    enterpriseName: response.enterpriseName,
    policyNumber: response.policyNumber,
    type: response.type,
    insurerName: response.insurerName,
    currentPremium: response.currentPremium,
    renewalPremium: response.renewalPremium,
    premiumVariation: response.premiumVariation,
    amendmentsTotal: response.amendmentsTotal,
    claimsTotal: response.claimsTotal,
    commissionPercentage: response.commissionPercentage,
    honoraryFee: response.honoraryFee,
    insurerFee: response.insurerFee,
    policyGuid: response.policyGuid,
  };
}

export async function fetchPaginatedRenewalsSummary(
  params: RenewalPaginationParams = {
    pageIndex: 0,
    pageSize: 50,
    sortBy: 'expiration',
    sortOrder: 'asc',
  },
  filters?: Filters,
  signal?: AbortSignal,
): Promise<Paginated<RenewalSummary>> {
  const queryParams: RenewalPaginationParams & Partial<Filters> = {
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...filters,
  };

  const response = await Client.get<PaginatedRenewalsResponse>({
    endpoint: `/renewals/summary/paginated`,
    params: queryParams,
    signal,
  });

  return {
    items: response.data.renewals.map(mapToRenewal),
    totalItems: response.data.totalRenewals,
    totalPages: response.data.totalPages,
  };
}
